"use client"

import { useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { LanguageSelector } from "@/components/docs/language-selector"
import { MarkdownRenderer } from "@/components/docs/markdown-renderer"
import { AlertCircle, Calendar, Edit, Eye, User } from "lucide-react"
import { DocumentationFeedback } from "./feedback"
import { ScrollArea } from "@/components/ui/scroll-area"

export function DocViewer({ documentation, session, activeArticle, activeSection }: {
  documentation: any,
  session: any,
  activeArticle?: string
  activeSection?: string
}) {
  const [selectedLanguage, setSelectedLanguage] = useState("en_us")
  const isCreator = session?.user?.id === documentation.createdById
  const isSuperAdmin = session?.user?.role === "SuperAdmin"
  const canEdit = isCreator || isSuperAdmin

  // Find the active article
  const currentArticle = documentation.sections
    .filter((section: any) => section.slug === activeSection)
    .flatMap((section: any) => section.articles)
    .find((article: any) => article.slug === activeArticle)

  // Format date
  const formatDate = (date: any) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  return (
    <ScrollArea type="always" className="w-full h-[calc(100vh-8rem)] flex-1 overflow-auto px-10 py-6 md:py-10">
      {!documentation.isPublished && (
        <Alert className="mb-6" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Not Published</AlertTitle>
          <AlertDescription>
            This documentation is not published yet and is only visible to its creator and super admins.
          </AlertDescription>
        </Alert>
      )}

      {currentArticle ? (
        <div className="space-y-8">
          <h2 className="text-xl font-bold">
            {currentArticle.title[selectedLanguage] || currentArticle.title.en_us}
          </h2>
          <div className="prose max-w-none dark:prose-invert">
            <MarkdownRenderer
              content={currentArticle.content[selectedLanguage] || currentArticle.content.en_us}
              images={currentArticle.images || []}
            />
          </div>
          <DocumentationFeedback documentationId={documentation.id} articleId={currentArticle.id} />
        </div>
      ) : (
        <>
          <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 overflow-hidden rounded-md">
                <Image
                  src={documentation.mod.icon || "/mod-placeholder.png"}
                  alt={documentation.mod.name}
                  width={64}
                  height={64}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold md:text-3xl">
                  {documentation.title[selectedLanguage] || documentation.title.en_us}
                </h1>
                <p className="text-muted-foreground">{documentation.mod.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector value={selectedLanguage} onChange={setSelectedLanguage} />
              {canEdit && (
                <Button variant="outline" size="sm" asChild>
                  <Link href={`/docs/${documentation.slug}/edit`}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div className="mb-6 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
            <p className="text-sm">{documentation.description[selectedLanguage] || documentation.description.en_us}</p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>By {documentation.createdBy.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>Created {formatDate(documentation.createdAt)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>Updated {formatDate(documentation.updatedAt)}</span>
              </div>
              {documentation.isPublished && (
                <Badge variant="outline" className="ml-auto">
                  <Eye className="mr-1 h-3 w-3" />
                  Published
                </Badge>
              )}
            </div>
          </div>
          <div className="rounded-lg border p-4 text-center">
            <p>No article selected. Please select an article from the sidebar.</p>
          </div>
        </>
      )}
    </ScrollArea>
  )
}
